import { Position } from "@xyflow/react";
import { absolutePosition } from "./auto-fuse";
import { detectCableBundles } from "./cable-bundles";
import { extractDensityTrunks } from "./cable-density-trunk";
import {
  planJunctionFanOut,
  planTerminalFanOut,
  type JunctionFan,
} from "./cable-fanout";
import { computeZoneTrunks, trunkCorridorsForCable, type Trunk } from "./cable-trunk";
import { planEscapeRoute } from "./cable-escape";
import {
  routeOrthogonal,
  sideDir,
  simplifyOrthogonal,
  type Pt,
} from "./cable-routing";
import { GEOMETRY } from "./canvas-geometry";
import { getDefinition } from "./catalog";
import { effectiveDimensions, effectiveTerminals } from "./effective";
import { markEnd, markStart } from "./perf-marks";
import { endpointPos } from "./store-helpers";
import { terminalPolarity } from "./terminal-snap";
import type { Cable, SystemNode, TerminalSide } from "./types";

export { planBusbarStudSwaps, planTerminalSwaps } from "./busbar-stud-swaps";
export type { CableEndpointUpdate } from "./busbar-stud-swaps";

type RailPolarity = "positive" | "negative" | "other";

function cableRailPolarity(cable: Cable, nodes: SystemNode[]): RailPolarity {
  const tryEnd = (nodeId: string, termId: string): RailPolarity => {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node || node.kind !== "component") return "other";
    const def = getDefinition(node.definitionId, node.customDefinition);
    if (!def || def.renderVariant === "junction") return "other";
    const term = effectiveTerminals(def, node).find((t) => t.id === termId);
    if (!term) return "other";
    return terminalPolarity(term);
  };
  const a = tryEnd(cable.fromNodeId, cable.fromTerminalId);
  if (a !== "other") return a;
  return tryEnd(cable.toNodeId, cable.toTerminalId);
}

/** Lane order inside a bundle: reds at one end, blacks at the other. */
function railPolarityRank(p: RailPolarity): number {
  if (p === "positive") return 0;
  if (p === "negative") return 2;
  return 1;
}

const {
  STUB,
  STUB_CLEARANCE,
  OBSTACLE_PADDING,
  SELF_PADDING,
  ELBOW_MARGIN,
  LANE_SPACING,
  POLARITY_LANE_SPACING,
  COLLISION_PENALTY,
  BODY_PENALTY,
  CROSSING_PENALTY,
  BEND_PENALTY,
  ZONE_WALL_PENALTY,
  ZONE_WALL_THICKNESS,
  CORRIDOR_OVERLAP_BONUS,
  CORRIDOR_MATCH_BAND_PX,
  SHARED_ELBOW_BONUS,
  CORRIDOR_DETOUR_SLACK_PX,
  DETOUR_CLAMP_RATIO,
  DETOUR_CLAMP_SLACK_PX,
} = GEOMETRY;

/** Bend coords of already-routed cables: `xs` from vertical segments, `ys` from
 *  horizontal ones. Rounded on insert and match, or near-identical stubs miss. */
export type SharedElbows = { xs: Set<number>; ys: Set<number> };

function emptyShared(): SharedElbows {
  return { xs: new Set(), ys: new Set() };
}

/** Stubs are included deliberately — cables leaving one terminal should rendezvous. */
function accumulateElbows(path: Pt[], shared: SharedElbows): void {
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i];
    const b = path[i + 1];
    if (a.x === b.x && a.y !== b.y) shared.xs.add(Math.round(a.x));
    else if (a.y === b.y && a.x !== b.x) shared.ys.add(Math.round(a.y));
  }
}

function countSharedElbowMatches(path: Pt[], shared: SharedElbows): number {
  if (shared.xs.size === 0 && shared.ys.size === 0) return 0;
  let n = 0;
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i];
    const b = path[i + 1];
    if (a.x === b.x && a.y !== b.y) {
      if (shared.xs.has(Math.round(a.x))) n++;
    } else if (a.y === b.y && a.x !== b.x) {
      if (shared.ys.has(Math.round(a.y))) n++;
    }
  }
  return n;
}

/** Signed perpendicular excursion of `path` away from the lane at `seg.coord`. */
function laneDestinationKey(path: Pt[], seg: SegRef): number {
  let key = 0;
  let bestAbs = 0;
  for (const p of path) {
    const d = seg.orientation === "h" ? p.y - seg.coord : p.x - seg.coord;
    if (Math.abs(d) > bestAbs) {
      bestAbs = Math.abs(d);
      key = d;
    }
  }
  return key;
}

/** `core` is the un-inflated body inside the padded rect; a rect without one is
 *  its own core. */
type Rect = {
  x: number;
  y: number;
  w: number;
  h: number;
  core?: { x: number; y: number; w: number; h: number };
};

function coreRect(r: Rect): { x: number; y: number; w: number; h: number } {
  return r.core ?? r;
}

function sideToPosition(side: TerminalSide): Position {
  switch (side) {
    case "left":
      return Position.Left;
    case "right":
      return Position.Right;
    case "top":
      return Position.Top;
    case "bottom":
      return Position.Bottom;
  }
}

type ObstacleSet = {
  padded: Map<string, Rect>;
  /** The cable's own source/target. Checked only on interior segments — a
   *  first/last stub always intersects its own terminal's body. */
  self: Map<string, Rect>;
  /** Four wall rects per zone; crossing one costs `ZONE_WALL_PENALTY`. */
  softByZone: Map<string, Rect[]>;
  zoneAncestorsByNode: Map<string, Set<string>>;
};

function zoneWallRects(x: number, y: number, w: number, h: number): Rect[] {
  const t = ZONE_WALL_THICKNESS;
  return [
    { x: x - t / 2, y: y - t / 2, w: w + t, h: t },
    { x: x - t / 2, y: y + h - t / 2, w: w + t, h: t },
    { x: x - t / 2, y: y - t / 2, w: t, h: h + t },
    { x: x + w - t / 2, y: y - t / 2, w: t, h: h + t },
  ];
}

function buildObstacles(nodes: SystemNode[]): ObstacleSet {
  const padded = new Map<string, Rect>();
  const self = new Map<string, Rect>();
  const softByZone = new Map<string, Rect[]>();
  for (const n of nodes) {
    if (n.kind === "component") {
      const def = getDefinition(n.definitionId, n.customDefinition);
      if (!def) continue;
      const abs = absolutePosition(n, nodes);
      const dims = effectiveDimensions(def, n);
      const body = { x: abs.x, y: abs.y, w: dims.width, h: dims.height };
      padded.set(n.id, {
        x: abs.x - OBSTACLE_PADDING,
        y: abs.y - OBSTACLE_PADDING,
        w: dims.width + 2 * OBSTACLE_PADDING,
        h: dims.height + 2 * OBSTACLE_PADDING,
        core: body,
      });
      self.set(n.id, {
        x: abs.x - SELF_PADDING,
        y: abs.y - SELF_PADDING,
        w: dims.width + 2 * SELF_PADDING,
        h: dims.height + 2 * SELF_PADDING,
        core: body,
      });
    } else if (n.kind === "zone") {
      const abs = absolutePosition(n, nodes);
      softByZone.set(n.id, zoneWallRects(abs.x, abs.y, n.width, n.height));
    }
  }
  const nodesById = new Map(nodes.map((n) => [n.id, n]));
  const zoneAncestorsByNode = new Map<string, Set<string>>();
  for (const n of nodes) {
    const ancestors = new Set<string>();
    let cur: SystemNode | undefined = n;
    while (cur && cur.parentId) {
      const parent = nodesById.get(cur.parentId);
      if (!parent) break;
      if (parent.kind === "zone") ancestors.add(parent.id);
      cur = parent;
    }
    zoneAncestorsByNode.set(n.id, ancestors);
  }
  return { padded, self, softByZone, zoneAncestorsByNode };
}

function getEndpointPosition(
  nodeId: string,
  terminalId: string,
  otherEnd: Pt | null,
  nodes: SystemNode[],
  avoidRects: Rect[] = [],
): { point: Pt; side: Position } | null {
  const node = nodes.find((n) => n.id === nodeId);
  if (!node) return null;
  const point = endpointPos(nodeId, terminalId, nodes);
  if (!point) return null;
  if (node.kind === "component") {
    const def = getDefinition(node.definitionId, node.customDefinition);
    if (!def) return null;
    if (def.renderVariant === "junction") {
      // Mirrors pickJunctionSide in canvas.tsx — keep the two in step.
      if (!otherEnd) return { point, side: Position.Right };
      const dx = otherEnd.x - point.x;
      const dy = otherEnd.y - point.y;
      // A junction is a point, so any approach is valid.
      const sides: Position[] = [
        Position.Right,
        Position.Left,
        Position.Bottom,
        Position.Top,
      ];
      const facing = (s: Position): number => {
        const d = sideDir(s);
        return d.x * dx + d.y * dy;
      };
      sides.sort((a, b) => facing(b) - facing(a));
      const stubClear = (s: Position): boolean => {
        if (avoidRects.length === 0) return true;
        const d = sideDir(s);
        const stubEnd: Pt = { x: point.x + d.x * STUB, y: point.y + d.y * STUB };
        return !avoidRects.some((r) => segmentInsideRect(point, stubEnd, r));
      };
      return { point, side: sides.find(stubClear) ?? sides[0] };
    }
    const term = effectiveTerminals(def, node).find((t) => t.id === terminalId);
    if (!term) return null;
    return { point, side: sideToPosition(term.side) };
  }
  return null;
}

function segmentInsideRect(a: Pt, b: Pt, r: Rect): boolean {
  const EPS = 0.001;
  // `simplifyOrthogonal` can leave a run a fraction of a pixel off-axis; a strict
  // axis test would make it invisible here and let it cross a body. Anything under
  // a pixel thick counts as axis-aligned; true diagonals still return false.
  const dx = Math.abs(b.x - a.x);
  const dy = Math.abs(b.y - a.y);
  if (dy <= 1 && dy <= dx) {
    const y0 = Math.min(a.y, b.y);
    const y1 = Math.max(a.y, b.y);
    if (y1 <= r.y + EPS || y0 >= r.y + r.h - EPS) return false;
    const x0 = Math.min(a.x, b.x);
    const x1 = Math.max(a.x, b.x);
    return x1 > r.x + EPS && x0 < r.x + r.w - EPS;
  }
  if (dx <= 1) {
    const x0 = Math.min(a.x, b.x);
    const x1 = Math.max(a.x, b.x);
    if (x1 <= r.x + EPS || x0 >= r.x + r.w - EPS) return false;
    const y0 = Math.min(a.y, b.y);
    const y1 = Math.max(a.y, b.y);
    return y1 > r.y + EPS && y0 < r.y + r.h - EPS;
  }
  return false;
}

function polyLength(pts: Pt[]): number {
  let total = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    total += Math.abs(pts[i + 1].x - pts[i].x) + Math.abs(pts[i + 1].y - pts[i].y);
  }
  return total;
}

function bendCount(pts: Pt[]): number {
  if (pts.length < 3) return 0;
  let bends = 0;
  for (let i = 1; i < pts.length - 1; i++) {
    const prev = pts[i - 1];
    const curr = pts[i];
    const next = pts[i + 1];
    const horiz1 = prev.y === curr.y;
    const horiz2 = curr.y === next.y;
    if (horiz1 !== horiz2) bends++;
  }
  return bends;
}

function segmentsCross(
  a1: Pt,
  a2: Pt,
  b1: Pt,
  b2: Pt,
): boolean {
  const aHoriz = a1.y === a2.y;
  const bHoriz = b1.y === b2.y;
  if (aHoriz === bHoriz) return false;
  const horiz = aHoriz ? { p1: a1, p2: a2 } : { p1: b1, p2: b2 };
  const vert = aHoriz ? { p1: b1, p2: b2 } : { p1: a1, p2: a2 };
  const hy = horiz.p1.y;
  const vx = vert.p1.x;
  const hx0 = Math.min(horiz.p1.x, horiz.p2.x);
  const hx1 = Math.max(horiz.p1.x, horiz.p2.x);
  const vy0 = Math.min(vert.p1.y, vert.p2.y);
  const vy1 = Math.max(vert.p1.y, vert.p2.y);
  // Endpoints touching don't count, or two cables meeting at a junction would.
  return vx > hx0 && vx < hx1 && hy > vy0 && hy < vy1;
}

function countCableCrossings(path: Pt[], others: Pt[][]): number {
  let n = 0;
  for (const other of others) {
    for (let i = 0; i < path.length - 1; i++) {
      for (let j = 0; j < other.length - 1; j++) {
        if (segmentsCross(path[i], path[i + 1], other[j], other[j + 1])) n++;
      }
    }
  }
  return n;
}

/** Segments sorted for binary search. Classification must match `segmentsCross`:
 *  horizontal iff the endpoints share a y, everything else vertical at its first x. */
type FlatSegments = {
  h: { y: number; lo: number; hi: number }[];
  v: { x: number; lo: number; hi: number }[];
};

function flattenSegments(paths: Pt[][]): FlatSegments {
  const h: FlatSegments["h"] = [];
  const v: FlatSegments["v"] = [];
  for (const path of paths) {
    for (let i = 0; i < path.length - 1; i++) {
      const a = path[i];
      const b = path[i + 1];
      if (a.y === b.y) {
        h.push({ y: a.y, lo: Math.min(a.x, b.x), hi: Math.max(a.x, b.x) });
      } else {
        v.push({ x: a.x, lo: Math.min(a.y, b.y), hi: Math.max(a.y, b.y) });
      }
    }
  }
  h.sort((p, q) => p.y - q.y);
  v.sort((p, q) => p.x - q.x);
  return { h, v };
}

/** First index whose `key` is strictly greater than `value` (`arr` sorted by it). */
function upperBound(
  arr: { [k: string]: number }[],
  key: "x" | "y",
  value: number,
): number {
  let lo = 0;
  let hi = arr.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid][key] <= value) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** Same count as `countCableCrossings(path, others)`, with `others` flattened. */
function countCrossingsFlat(path: Pt[], flat: FlatSegments): number {
  let n = 0;
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i];
    const b = path[i + 1];
    if (a.y === b.y) {
      const y = a.y;
      const x0 = Math.min(a.x, b.x);
      const x1 = Math.max(a.x, b.x);
      for (let k = upperBound(flat.v, "x", x0); k < flat.v.length; k++) {
        const seg = flat.v[k];
        if (seg.x >= x1) break;
        if (y > seg.lo && y < seg.hi) n++;
      }
    } else {
      const x = a.x;
      const y0 = Math.min(a.y, b.y);
      const y1 = Math.max(a.y, b.y);
      for (let k = upperBound(flat.h, "y", y0); k < flat.h.length; k++) {
        const seg = flat.h[k];
        if (seg.y >= y1) break;
        if (x > seg.lo && x < seg.hi) n++;
      }
    }
  }
  return n;
}

/** Stacked = same line, close enough that one cable hides the other. `spread`'s
 *  outsider test hardcodes the same 8 px overlap — keep the two in step. */
const STACK_THRESH_PX = 5;
const STACK_MIN_OVERLAP_PX = 8;

function countCableStacks(path: Pt[], others: Pt[][]): number {
  let n = 0;
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i];
    const b = path[i + 1];
    const horiz = a.y === b.y && a.x !== b.x;
    const vert = a.x === b.x && a.y !== b.y;
    if (!horiz && !vert) continue;
    const coord = horiz ? a.y : a.x;
    const lo = horiz ? Math.min(a.x, b.x) : Math.min(a.y, b.y);
    const hi = horiz ? Math.max(a.x, b.x) : Math.max(a.y, b.y);
    for (const other of others) {
      for (let j = 0; j < other.length - 1; j++) {
        const p = other[j];
        const q = other[j + 1];
        const pHoriz = p.y === q.y && p.x !== q.x;
        const pVert = p.x === q.x && p.y !== q.y;
        if (horiz !== pHoriz || vert !== pVert) continue;
        if (!pHoriz && !pVert) continue;
        if (Math.abs((pHoriz ? p.y : p.x) - coord) >= STACK_THRESH_PX) continue;
        const pLo = pHoriz ? Math.min(p.x, q.x) : Math.min(p.y, q.y);
        const pHi = pHoriz ? Math.max(p.x, q.x) : Math.max(p.y, q.y);
        if (Math.min(hi, pHi) - Math.max(lo, pLo) > STACK_MIN_OVERLAP_PX) n++;
      }
    }
  }
  return n;
}

/** Total length `path` runs alongside a corridor, within CORRIDOR_MATCH_BAND_PX. */
function totalCorridorOverlap(path: Pt[], corridors: Pt[][]): number {
  let total = 0;
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i];
    const b = path[i + 1];
    const aHoriz = a.y === b.y && a.x !== b.x;
    const aVert = a.x === b.x && a.y !== b.y;
    if (!aHoriz && !aVert) continue;
    const aPerp = aHoriz ? a.y : a.x;
    const aLo = aHoriz ? Math.min(a.x, b.x) : Math.min(a.y, b.y);
    const aHi = aHoriz ? Math.max(a.x, b.x) : Math.max(a.y, b.y);
    for (const corridor of corridors) {
      for (let j = 0; j < corridor.length - 1; j++) {
        const p = corridor[j];
        const q = corridor[j + 1];
        const pHoriz = p.y === q.y && p.x !== q.x;
        const pVert = p.x === q.x && p.y !== q.y;
        if (!pHoriz && !pVert) continue;
        if (aHoriz !== pHoriz) continue;
        const pPerp = pHoriz ? p.y : p.x;
        if (Math.abs(aPerp - pPerp) > CORRIDOR_MATCH_BAND_PX) continue;
        const pLo = pHoriz ? Math.min(p.x, q.x) : Math.min(p.y, q.y);
        const pHi = pHoriz ? Math.max(p.x, q.x) : Math.max(p.y, q.y);
        const overlap = Math.min(aHi, pHi) - Math.max(aLo, pLo);
        if (overlap > 0) total += overlap;
      }
    }
  }
  return total;
}

/** Inclusive point-in-rect (boundary counts as inside). */
function pointInRect(p: Pt, r: Rect): boolean {
  const EPS = 0.001;
  return (
    p.x >= r.x - EPS &&
    p.x <= r.x + r.w + EPS &&
    p.y >= r.y - EPS &&
    p.y <= r.y + r.h + EPS
  );
}

/** `padded` counts every entry into an inflated obstacle rect, `body` the
 *  subset that reaches the component itself. */
type CollisionCounts = { padded: number; body: number };

function countCollisions(
  pts: Pt[],
  hard: Rect[],
  self: Rect[] = [],
): CollisionCounts {
  let n = 0;
  let body = 0;
  const hitsBody = (a: Pt, b: Pt, r: Rect): boolean =>
    r.core === undefined || segmentInsideRect(a, b, r.core);
  const lastIdx = pts.length - 2;
  for (let i = 0; i <= lastIdx; i++) {
    for (const r of hard) {
      if (!segmentInsideRect(pts[i], pts[i + 1], r)) continue;
      n++;
      if (hitsBody(pts[i], pts[i + 1], r)) body++;
    }
    for (const r of self) {
      // A terminal exit spends its own clearance, but must still leave the body
      // outwards. Simplifying a reversed stub can otherwise turn the last run
      // into a wire entering the far side of its own component.
      const endpoint = i === 0 && pointInRect(pts[0], r) ? pts[0]
        : i === lastIdx && pointInRect(pts[lastIdx + 1], r) ? pts[lastIdx + 1] : null;
      if (endpoint) {
        // Junctions have a centre terminal inside their small body.
        if (!strictlyInside(endpoint, coreRect(r)) && segmentInsideRect(pts[i], pts[i + 1], coreRect(r))) {
          n++;
          body++;
        }
        continue;
      }
      if (!segmentInsideRect(pts[i], pts[i + 1], r)) continue;
      n++;
      if (hitsBody(pts[i], pts[i + 1], r)) body++;
    }
  }
  return { padded: n, body };
}

/** Longest stub ≤ `STUB` that leaves `p` along `d` without entering a body the
 *  cable doesn't terminate on — `STUB` (18) exceeds the 10–16 px gap between
 *  components parked side by side. Measured against bodies, not padded rects: a
 *  stub may spend a neighbour's clearance, not enter it. */
function clampStub(
  p: Pt,
  d: Pt,
  hard: readonly Rect[],
  self: readonly Rect[],
): number {
  let limit: number = STUB;
  for (const r of hard) {
    const core = coreRect(r);
    if (strictlyInside(p, core)) return 0;
    limit = Math.min(limit, stubRoom(p, d, core));
  }
  for (const r of self) {
    const core = coreRect(r);
    if (strictlyInside(p, core)) continue;
    limit = Math.min(limit, stubRoom(p, d, core));
  }
  return Math.max(0, limit);
}

/** Strict point-in-rect. A terminal flush against a neighbour's edge counts as
 *  outside, or `clampStub` would collapse a perfectly good stub to zero. */
function strictlyInside(
  p: Pt,
  r: { x: number; y: number; w: number; h: number },
): boolean {
  const EPS = 0.001;
  return (
    p.x > r.x + EPS &&
    p.x < r.x + r.w - EPS &&
    p.y > r.y + EPS &&
    p.y < r.y + r.h - EPS
  );
}

/** Distance from `p` along cardinal `d` to `core`'s near edge, less the clearance
 *  kept off it. `Infinity` when the ray misses or the rect is behind `p`. */
function stubRoom(
  p: Pt,
  d: Pt,
  core: { x: number; y: number; w: number; h: number },
): number {
  let dist: number;
  if (d.x !== 0) {
    if (p.y <= core.y || p.y >= core.y + core.h) return Infinity;
    dist = d.x > 0 ? core.x - p.x : p.x - (core.x + core.w);
  } else {
    if (p.x <= core.x || p.x >= core.x + core.w) return Infinity;
    dist = d.y > 0 ? core.y - p.y : p.y - (core.y + core.h);
  }
  return dist < 0 ? Infinity : dist - STUB_CLEARANCE;
}

function countSoftCollisions(pts: Pt[], soft: Rect[]): number {
  if (soft.length === 0) return 0;
  let n = 0;
  for (let i = 0; i < pts.length - 1; i++) {
    for (const r of soft) {
      if (segmentInsideRect(pts[i], pts[i + 1], r)) n++;
    }
  }
  return n;
}

function buildCandidates(
  source: Pt,
  sourceSide: Position,
  target: Pt,
  targetSide: Position,
  hard: Rect[],
  self: Rect[],
  soft: Rect[] = [],
  corridors: Pt[][] = [],
  shared: SharedElbows = emptyShared(),
  sourceStub: number = STUB,
  targetStub: number = STUB,
): Pt[][] {
  const sDir = sideDir(sourceSide);
  const tDir = sideDir(targetSide);
  const sa: Pt = {
    x: source.x + sDir.x * sourceStub,
    y: source.y + sDir.y * sourceStub,
  };
  const ta: Pt = {
    x: target.x + tDir.x * targetStub,
    y: target.y + tDir.y * targetStub,
  };

  const xElbows = new Set<number>([(sa.x + ta.x) / 2, sa.x, ta.x]);
  const yElbows = new Set<number>([(sa.y + ta.y) / 2, sa.y, ta.y]);
  for (const r of hard) {
    xElbows.add(r.x - ELBOW_MARGIN);
    xElbows.add(r.x + r.w + ELBOW_MARGIN);
    yElbows.add(r.y - ELBOW_MARGIN);
    yElbows.add(r.y + r.h + ELBOW_MARGIN);
  }
  for (const r of self) {
    xElbows.add(r.x - ELBOW_MARGIN);
    xElbows.add(r.x + r.w + ELBOW_MARGIN);
    yElbows.add(r.y - ELBOW_MARGIN);
    yElbows.add(r.y + r.h + ELBOW_MARGIN);
  }
  // Seed an elbow on each zone wall line, or "exit at this corner" isn't a candidate.
  for (const r of soft) {
    xElbows.add(r.x);
    xElbows.add(r.x + r.w);
    yElbows.add(r.y);
    yElbows.add(r.y + r.h);
  }
  // ±LANE_SPACING beside the corridor line lets a follower hug its primary one lane over.
  for (const c of corridors) {
    for (let i = 0; i < c.length - 1; i++) {
      const a = c[i];
      const b = c[i + 1];
      if (a.x === b.x && a.y !== b.y) {
        xElbows.add(a.x);
        xElbows.add(a.x + LANE_SPACING);
        xElbows.add(a.x - LANE_SPACING);
      } else if (a.y === b.y && a.x !== b.x) {
        yElbows.add(a.y);
        yElbows.add(a.y + LANE_SPACING);
        yElbows.add(a.y - LANE_SPACING);
      }
    }
  }
  for (const x of shared.xs) xElbows.add(x);
  for (const y of shared.ys) yElbows.add(y);

  const out: Pt[][] = [];
  for (const ex of xElbows) {
    out.push([source, sa, { x: ex, y: sa.y }, { x: ex, y: ta.y }, ta, target]);
  }
  for (const ey of yElbows) {
    out.push([source, sa, { x: sa.x, y: ey }, { x: ta.x, y: ey }, ta, target]);
  }
  out.push([source, sa, { x: sa.x, y: ta.y }, ta, target]);
  out.push([source, sa, { x: ta.x, y: sa.y }, ta, target]);

  // Scoring is the expensive part, so drop duplicates. First occurrence wins,
  // preserving the scoring tie-break order.
  const seen = new Set<string>();
  const unique: Pt[][] = [];
  for (const pts of out) {
    const simplified = simplifyOrthogonal(pts);
    let key = "";
    for (const p of simplified) key += `${p.x},${p.y};`;
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(simplified);
  }
  return unique;
}

/** Lowest-cost orthogonal route. `others` are paths already routed in this pass.
 *  `sourceSplay`/`targetSplay` (px) shift the search endpoints along the body edge
 *  so cables sharing a terminal exit at distinct points; the real terminal coords
 *  are stitched back on so React Flow's edge endpoints still match. */
export type EscapePlanner = typeof planEscapeRoute;

export function tidyRoute(
  source: Pt,
  sourceSide: Position,
  target: Pt,
  targetSide: Position,
  hard: Rect[],
  self: Rect[] = [],
  others: Pt[][] = [],
  sourceSplay = 0,
  targetSplay = 0,
  soft: Rect[] = [],
  corridors: Pt[][] = [],
  shared: SharedElbows = emptyShared(),
  escapePlanner: EscapePlanner = planEscapeRoute,
): Pt[] {
  const effSource =
    sourceSplay !== 0 ? splayAlongSide(source, sourceSide, sourceSplay) : source;
  const effTarget =
    targetSplay !== 0 ? splayAlongSide(target, targetSide, targetSplay) : target;
  const sourceStub = clampStub(effSource, sideDir(sourceSide), hard, self);
  const targetStub = clampStub(effTarget, sideDir(targetSide), hard, self);
  const candidates = buildCandidates(
    effSource,
    sourceSide,
    effTarget,
    targetSide,
    hard,
    self,
    soft,
    corridors,
    shared,
    sourceStub,
    targetStub,
  );
  // Exact: a rect outside every candidate's bounding box can't intersect one.
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const c of candidates) {
    for (const p of c) {
      if (p.x < minX) minX = p.x;
      if (p.x > maxX) maxX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.y > maxY) maxY = p.y;
    }
  }
  const nearby = (r: Rect): boolean =>
    r.x < maxX && r.x + r.w > minX && r.y < maxY && r.y + r.h > minY;
  const hardNear = hard.length > 8 ? hard.filter(nearby) : hard;
  const softNear = soft.length > 8 ? soft.filter(nearby) : soft;

  const hasShared = shared.xs.size > 0 || shared.ys.size > 0;
  const othersFlat = others.length > 0 ? flattenSegments(others) : null;
  const directLen =
    Math.abs(effTarget.x - effSource.x) + Math.abs(effTarget.y - effSource.y);
  const scoreOf = (c: Pt[]): { score: number; body: number } => {
    const { padded: collisions, body } = countCollisions(c, hardNear, self);
    const softCollisions = countSoftCollisions(c, softNear);
    const crossings = othersFlat ? countCrossingsFlat(c, othersFlat) : 0;
    const bends = bendCount(c);
    const corridorOverlap =
      corridors.length > 0 ? totalCorridorOverlap(c, corridors) : 0;
    const sharedMatches = hasShared ? countSharedElbowMatches(c, shared) : 0;
    const len = polyLength(c);
    // The bonuses scale with distance run alongside, so an unbounded detour earns
    // enough to cancel its own length penalty. Withdraw them past direct + slack;
    // hugging a primary shifts sideways, so real bundling keeps its reward.
    const rawBonus =
      corridorOverlap * CORRIDOR_OVERLAP_BONUS +
      sharedMatches * SHARED_ELBOW_BONUS;
    const bonus = len <= directLen + CORRIDOR_DETOUR_SLACK_PX ? rawBonus : 0;
    const score =
      body * BODY_PENALTY +
      collisions * COLLISION_PENALTY +
      softCollisions * ZONE_WALL_PENALTY +
      crossings * CROSSING_PENALTY +
      bends * BEND_PENALTY +
      len -
      bonus;
    return { score, body };
  };

  let best = candidates[0];
  let bestScore = Infinity;
  let bestBody = Infinity;
  for (const c of candidates) {
    const { score, body } = scoreOf(c);
    if (score < bestScore) {
      bestScore = score;
      bestBody = body;
      best = c;
    }
  }
  // The candidates only span Z- and L-routes with one free mid-line, so a detour
  // needing two free coords isn't among them. Gated on a body hit, not any
  // collision: clearance loss is routine and would buy A* on most cables.
  if (bestBody > 0) {
    const escape = escapePlanner(
      effSource,
      sourceSide,
      effTarget,
      targetSide,
      hard,
      self,
      soft,
      sourceStub,
      targetStub,
    );
    if (escape) {
      const { score } = scoreOf(escape);
      if (score < bestScore) best = escape;
    }
  }
  if (sourceSplay === 0 && targetSplay === 0) return best;
  // Lane separation and the self-collision check both already skip the first and
  // last segment, so the stitched corners cost nothing.
  let stitched: Pt[] = best;
  if (sourceSplay !== 0) stitched = [source, ...stitched];
  if (targetSplay !== 0) stitched = [...stitched, target];
  return simplifyOrthogonal(stitched);
}

/** Shift a terminal coord by `splay` px along the body edge it sits on. */
function splayAlongSide(p: Pt, side: Position, splay: number): Pt {
  const sDir = sideDir(side);
  const tan = { x: -sDir.y, y: sDir.x };
  return { x: p.x + tan.x * splay, y: p.y + tan.y * splay };
}

type SegRef = {
  cableId: string;
  segIdx: number;
  orientation: "h" | "v";
  coord: number;
  lo: number;
  hi: number;
};

type CableObstacles = {
  /** Every component the cable doesn't touch. Applies on every segment. */
  hard: Rect[];
  /** The cable's own source/target. Interior segments only. */
  self: Rect[];
  /** Walls of the zones separating the two endpoints. A zone containing both
   *  is excluded, so a run inside one zone never pays to enter or leave it. */
  soft: Rect[];
};

export function obstaclesForCable(
  cable: CableEndpoints,
  obstacles: ObstacleSet,
): CableObstacles {
  const hard: Rect[] = [];
  for (const [id, rect] of obstacles.padded) {
    if (id === cable.fromNodeId || id === cable.toNodeId) continue;
    hard.push(rect);
  }
  const self: Rect[] = [];
  const fromSelf = obstacles.self.get(cable.fromNodeId);
  const toSelf = obstacles.self.get(cable.toNodeId);
  if (fromSelf) self.push(fromSelf);
  if (toSelf && toSelf !== fromSelf) self.push(toSelf);
  const fromAncestors = obstacles.zoneAncestorsByNode.get(cable.fromNodeId);
  const toAncestors = obstacles.zoneAncestorsByNode.get(cable.toNodeId);
  const soft: Rect[] = [];
  for (const [zoneId, walls] of obstacles.softByZone) {
    const fromInside = fromAncestors?.has(zoneId) ?? false;
    const toInside = toAncestors?.has(zoneId) ?? false;
    if (fromInside && toInside) continue;
    soft.push(...walls);
  }
  return { hard, self, soft };
}

/** Wider than `Cable`: a preview connection isn't a persisted cable yet. */
type CableEndpoints = Pick<Cable, "fromNodeId" | "toNodeId">;

/** Component/zone geometry for preview routing. O(nodes) — build once per
 *  system change, not once per cable. */
export type PreviewObstacles = ObstacleSet;

export function buildPreviewObstacles(nodes: SystemNode[]): PreviewObstacles {
  return buildObstacles(nodes);
}

/** Bodies alone — no crossings, corridors, bundles or lane separation;
 *  `tidySystemCables` owns settled routes. Rects come from stored node positions,
 *  so a drag routes against stale ones until the drop-time tidy corrects it. */
export function previewRoute(
  obstacles: PreviewObstacles,
  cable: CableEndpoints,
  source: Pt,
  sourceSide: Position,
  target: Pt,
  targetSide: Position,
): Pt[] {
  const co = obstaclesForCable(cable, obstacles);
  return tidyRoute(
    source,
    sourceSide,
    target,
    targetSide,
    co.hard,
    co.self,
    [],
    0,
    0,
    co.soft,
  );
}

/** Nudge interior parallel segments apart so co-travelling cables don't render
 *  on top of each other. */
function applyLaneSeparation(
  paths: Map<string, Pt[]>,
  cablesById: Map<string, Cable>,
  obstacles: ObstacleSet,
  junctionFan: Map<string, JunctionFan>,
  cablePolarities: Map<string, RailPolarity>,
  fixedPaths: Pt[][] = [],
  separateEndpointRuns = false,
): void {
  const all: SegRef[] = [];
  for (const [cableId, path] of paths) {
    const fan = junctionFan.get(cableId);
    const numSegs = path.length - 1;
    // Skip the first/last stubs — they're tied to terminal positions.
    for (let i = 1; i < numSegs - 1; i++) {
      // Skip the segment ending at a junction fan anchor: nudging it moves the
      // anchor of the diagonal final segment and warps the fan angle.
      if (fan?.source && i === 1) continue;
      if (fan?.target && i === numSegs - 2) continue;
      const a = path[i];
      const b = path[i + 1];
      if (a.x === b.x && a.y !== b.y) {
        all.push({
          cableId,
          segIdx: i,
          orientation: "v",
          coord: a.x,
          lo: Math.min(a.y, b.y),
          hi: Math.max(a.y, b.y),
        });
      } else if (a.y === b.y && a.x !== b.x) {
        all.push({
          cableId,
          segIdx: i,
          orientation: "h",
          coord: a.y,
          lo: Math.min(a.x, b.x),
          hi: Math.max(a.x, b.x),
        });
      }
    }
  }

  // Single-link clustering within LANE_SPACING, not fixed-grid bucketing: a grid
  // puts y=14.9 and y=15.1 in different buckets and never separates them.
  const byOrientation = new Map<"h" | "v", SegRef[]>();
  for (const s of all) {
    const list = byOrientation.get(s.orientation) ?? [];
    list.push(s);
    byOrientation.set(s.orientation, list);
  }
  const buckets: SegRef[][] = [];
  for (const list of byOrientation.values()) {
    list.sort((a, b) => a.coord - b.coord || a.cableId.localeCompare(b.cableId));
    let cur: SegRef[] = [];
    let lastCoord = Number.NEGATIVE_INFINITY;
    for (const s of list) {
      if (cur.length > 0 && s.coord - lastCoord > LANE_SPACING) {
        buckets.push(cur);
        cur = [];
      }
      cur.push(s);
      lastCoord = s.coord;
    }
    if (cur.length > 0) buckets.push(cur);
  }

  for (const segs of buckets) {
    if (segs.length < 2 && !separateEndpointRuns) continue;
    segs.sort((a, b) => a.lo - b.lo || a.cableId.localeCompare(b.cableId));
    let cluster: SegRef[] = [];
    let clusterHi = -Infinity;
    const flush = () => {
      // A lone interior segment can still sit on another wire's long terminal
      // run. Those runs remain fixed, but count as outsiders when this lane moves.
      if (cluster.length > 1 || (separateEndpointRuns && cluster.length === 1))
        spread(cluster, paths, cablesById, obstacles, cablePolarities, fixedPaths);
      cluster = [];
      clusterHi = -Infinity;
    };
    for (const s of segs) {
      if (s.lo <= clusterHi) {
        cluster.push(s);
        clusterHi = Math.max(clusterHi, s.hi);
      } else {
        flush();
        cluster = [s];
        clusterHi = s.hi;
      }
    }
    flush();
  }
}

/** How many crossings one stacked cable is worth. Shared by `spread` and the
 *  crossing-repair loop so the two passes optimise the same thing. */
const STACK_WEIGHT = 3;

function spread(
  group: SegRef[],
  paths: Map<string, Pt[]>,
  cablesById: Map<string, Cable>,
  obstacles: ObstacleSet,
  cablePolarities: Map<string, RailPolarity>,
  fixedPaths: Pt[][] = [],
): void {
  const n = group.length;
  if (n === 0) return;
  // Polarity first so reds and blacks don't interleave, then signed excursion —
  // the one order with no crossings inside the bundle. Id breaks the last tie.
  group.sort((a, b) => {
    const pa = railPolarityRank(cablePolarities.get(a.cableId) ?? "other");
    const pb = railPolarityRank(cablePolarities.get(b.cableId) ?? "other");
    if (pa !== pb) return pa - pb;
    const pathA = paths.get(a.cableId);
    const pathB = paths.get(b.cableId);
    const keyA = pathA ? laneDestinationKey(pathA, a) : 0;
    const keyB = pathB ? laneDestinationKey(pathB, b) : 0;
    if (keyA !== keyB) return keyA - keyB;
    return a.cableId.localeCompare(b.cableId);
  });

  const orientation = group[0].orientation;
  const items = group.map((seg) => {
    const path = paths.get(seg.cableId);
    const cable = cablesById.get(seg.cableId);
    const co = cable
      ? obstaclesForCable(cable, obstacles)
      : { hard: [] as Rect[], self: [] as Rect[], soft: [] as Rect[] };
    // A lane slides only along its perpendicular axis, so a rect missing the
    // segment's own-axis extent can never be hit whatever lane is picked.
    const span =
      orientation === "h"
        ? { lo: seg.lo, hi: seg.hi, axis: "x" as const }
        : { lo: seg.lo, hi: seg.hi, axis: "y" as const };
    const overlaps = (r: Rect): boolean =>
      span.axis === "x"
        ? r.x < span.hi && r.x + r.w > span.lo
        : r.y < span.hi && r.y + r.h > span.lo;
    return {
      seg,
      path,
      co: {
        hard: co.hard.length > 8 ? co.hard.filter(overlaps) : co.hard,
        self: co.self,
      },
    };
  });

  const coords = items.map((it) => it.seg.coord);
  const clusterCoord = coords.reduce((s, c) => s + c, 0) / n;
  const polarities = items.map((it) => cablePolarities.get(it.seg.cableId) ?? "other");
  const firstNegative = polarities.indexOf("negative");
  const hasBothRails = firstNegative !== -1 && polarities.includes("positive");
  const oppositePairs: [number, number][] = [];
  if (hasBothRails) {
    for (let i = 0; i < n; i++) {
      if (polarities[i] !== "positive") continue;
      for (let j = firstNegative; j < n; j++) {
        const a = items[i].seg;
        const b = items[j].seg;
        if (Math.min(a.hi, b.hi) > Math.max(a.lo, b.lo)) oppositePairs.push([i, j]);
      }
    }
  }

  const clusterIds = new Set(group.map((g) => g.cableId));
  const otherPaths: Pt[][] = [];
  // Same-orientation segments outside this cluster, so a slide doesn't land a lane
  // on top of one of them.
  const otherSegs: { coord: number; lo: number; hi: number }[] = [];
  const collectOther = (ppath: Pt[]): void => {
    otherPaths.push(ppath);
    for (let k = 0; k < ppath.length - 1; k++) {
      const p = ppath[k];
      const q = ppath[k + 1];
      if (orientation === "v" && p.x === q.x && p.y !== q.y)
        otherSegs.push({ coord: p.x, lo: Math.min(p.y, q.y), hi: Math.max(p.y, q.y) });
      else if (orientation === "h" && p.y === q.y && p.x !== q.x)
        otherSegs.push({ coord: p.y, lo: Math.min(p.x, q.x), hi: Math.max(p.x, q.x) });
    }
  };
  for (const [pid, ppath] of paths) {
    if (clusterIds.has(pid)) continue;
    collectOther(ppath);
  }
  // Cables this pass isn't allowed to move count as outsiders too.
  for (const fpath of fixedPaths) collectOther(fpath);
  const otherFlat = otherPaths.length > 0 ? flattenSegments(otherPaths) : null;
  const STACK_THRESH = STACK_THRESH_PX;
  const stacksOutsider = (target: number, lo: number, hi: number): boolean => {
    for (const o of otherSegs) {
      if (Math.abs(o.coord - target) >= STACK_THRESH) continue;
      if (Math.min(hi, o.hi) - Math.max(lo, o.lo) > 8) return true;
    }
    return false;
  };

  const laneSeg = (item: (typeof items)[number], target: number): [Pt, Pt] | null => {
    if (!item.path) return null;
    const a = item.path[item.seg.segIdx];
    const b = item.path[item.seg.segIdx + 1];
    return orientation === "h"
      ? [
          { x: a.x, y: target },
          { x: b.x, y: target },
        ]
      : [
          { x: target, y: a.y },
          { x: target, y: b.y },
        ];
  };
  // Sliding a lane stretches the two connectors joining it to the route, so score
  // the whole window they disturb, not just the moved segment. Both neighbours
  // exist: the caller skips the stubs, so segIdx ≥ 1 and segIdx + 1 ≤ numSegs-2.
  const laneWindow = (item: (typeof items)[number], seg: [Pt, Pt]): Pt[] | null => {
    if (!item.path) return null;
    return [item.path[item.seg.segIdx - 1], seg[0], seg[1], item.path[item.seg.segIdx + 2]];
  };
  /** Would the lane itself land inside a component? A hard block — it stays at
   *  its origin; `connectorHitsBody` prices the two connectors instead. */
  const hits = (item: (typeof items)[number], seg: [Pt, Pt]): boolean =>
    item.co.hard.some((r) => segmentInsideRect(seg[0], seg[1], r)) ||
    item.co.self.some((r) => segmentInsideRect(seg[0], seg[1], r));
  /** Priced, not blocked — blocking leaves the cable stacked where it was, which
   *  is what this pass exists to prevent. `self` is skipped on the first and last
   *  segment, as `countCollisions` treats it. */
  const connectorHitsBody = (
    item: (typeof items)[number],
    seg: [Pt, Pt],
  ): boolean => {
    const window = laneWindow(item, seg);
    if (!window) return false;
    const lastSegIdx = (item.path?.length ?? 2) - 2;
    for (const i of [0, 2]) {
      const a = window[i];
      const b = window[i + 1];
      if (item.co.hard.some((r) => segmentInsideRect(a, b, r))) return true;
      const pathIdx = item.seg.segIdx - 1 + i;
      if (pathIdx === 0 || pathIdx === lastSegIdx) continue;
      if (item.co.self.some((r) => segmentInsideRect(a, b, r))) return true;
    }
    return false;
  };
  /** High enough that a clean alternative wins, finite so a boxed-in cluster
   *  still separates. */
  const CONNECTOR_BODY_COST = 12;
  const origSeg = items.map((it): [Pt, Pt] | null =>
    it.path ? [it.path[it.seg.segIdx], it.path[it.seg.segIdx + 1]] : null,
  );

  // The cables sit in consecutive lanes and the block slides as a unit, keeping
  // the crossing-minimising order. A lane that can't escape a body counts as
  // stacked at its origin, so an un-appliable shift never scores as an
  // improvement; the identity assignment is one of the candidates, without which
  // the search could only choose how to spread, never whether to.
  const SPACINGS = [LANE_SPACING, 7, 5];
  /** Mates count on *any* projection overlap, not the outsider threshold — a
   *  stricter re-test would let a marginal pair opt out of separation entirely. */
  const isStacked = (i: number, coordOf: number[]): boolean => {
    const si = items[i].seg;
    if (stacksOutsider(coordOf[i], si.lo, si.hi)) return true;
    for (let j = 0; j < n; j++) {
      if (j === i) continue;
      if (Math.abs(coordOf[j] - coordOf[i]) >= STACK_THRESH) continue;
      const sj = items[j].seg;
      if (Math.min(si.hi, sj.hi) - Math.max(si.lo, sj.lo) > 0) return true;
    }
    return false;
  };
  const costOf = (targets: number[], extra: number): number => {
    const effective: number[] = [];
    const segs: ([Pt, Pt] | null)[] = [];
    for (let i = 0; i < n; i++) {
      const target = laneSeg(items[i], targets[i]);
      const blocked = !target || hits(items[i], target);
      effective.push(blocked ? items[i].seg.coord : targets[i]);
      segs.push(blocked ? origSeg[i] : target);
    }
    let stacked = 0;
    let crossings = 0;
    let bodies = 0;
    for (let i = 0; i < n; i++) {
      if (isStacked(i, effective)) stacked++;
      const seg = segs[i];
      if (!seg) continue;
      const window = laneWindow(items[i], seg);
      if (window && otherFlat) crossings += countCrossingsFlat(window, otherFlat);
      if (connectorHitsBody(items[i], seg)) bodies++;
    }
    // Whitespace is a preference, never a reason for another crossing or body
    // collision. Bound the entire group's penalty below the cost of one crossing.
    const railCrowding = oppositePairs.reduce((worst, [i, j]) => Math.max(
      worst,
      Math.max(0, POLARITY_LANE_SPACING - Math.abs(effective[j] - effective[i])) /
        POLARITY_LANE_SPACING,
    ), 0);
    return STACK_WEIGHT * stacked + crossings + CONNECTOR_BODY_COST * bodies +
      0.25 * railCrowding + extra;
  };

  const identityTargets = items.map((it) => it.seg.coord);
  let best: { targets: number[]; cost: number } = {
    targets: identityTargets,
    cost: costOf(identityTargets, 0),
  };
  for (const spacing of SPACINGS) {
    // Keep the compact candidates too: preferred rail separation must not push
    // a cable through an obstacle or introduce a crossing in a narrow corridor.
    const railGaps = hasBothRails ? [POLARITY_LANE_SPACING - spacing, 0] : [0];
    for (const railGap of railGaps) {
      const centeredBase = -((n - 1) * spacing + railGap) / 2;
      const tighten = (LANE_SPACING - spacing) * 0.5;
      const SEARCH = n + 6;
      for (let s = -SEARCH; s <= SEARCH; s++) {
        const base = centeredBase + s * spacing;
        const disp = Math.abs(base - centeredBase) / LANE_SPACING;
        const targets = items.map((_, i) => clusterCoord + base + i * spacing +
          (hasBothRails && i >= firstNegative ? railGap : 0));
        const cost = costOf(targets, 0.05 * disp + tighten);
        if (cost < best.cost - 1e-9) best = { targets, cost };
      }
    }
  }
  if (best.targets === identityTargets) return;

  for (let i = 0; i < n; i++) {
    const item = items[i];
    if (!item.path) continue;
    const seg = laneSeg(item, best.targets[i]);
    if (!seg) continue;
    // Boxed in on both sides: leave the lane rather than shove it through a body.
    if (hits(item, seg)) continue;
    item.path[item.seg.segIdx] = seg[0];
    item.path[item.seg.segIdx + 1] = seg[1];
  }
}

/** Ids of cables whose path crosses the body of any given node, so a node-move
 *  tidy can widen its scope. Raw body rect, no padding, so a cable passing nearby
 *  isn't churned; cables touching one of the nodes are excluded. */
export function cablesThroughNodes(
  nodes: SystemNode[],
  cables: Cable[],
  nodeIds: Iterable<string>,
): string[] {
  const idSet = new Set(nodeIds);
  const rects: Rect[] = [];
  for (const n of nodes) {
    if (!idSet.has(n.id) || n.kind !== "component") continue;
    const def = getDefinition(n.definitionId, n.customDefinition);
    if (!def) continue;
    const abs = absolutePosition(n, nodes);
    const dims = effectiveDimensions(def, n);
    rects.push({ x: abs.x, y: abs.y, w: dims.width, h: dims.height });
  }
  if (rects.length === 0) return [];
  const out: string[] = [];
  for (const cable of cables) {
    if (idSet.has(cable.fromNodeId) || idSet.has(cable.toNodeId)) continue;
    const fromPoint = endpointPos(cable.fromNodeId, cable.fromTerminalId, nodes);
    const toPoint = endpointPos(cable.toNodeId, cable.toTerminalId, nodes);
    if (!fromPoint || !toPoint) continue;
    let poly: Pt[];
    if (cable.waypoints && cable.waypoints.length > 0) {
      poly = [fromPoint, ...cable.waypoints, toPoint];
    } else {
      const from = getEndpointPosition(
        cable.fromNodeId,
        cable.fromTerminalId,
        toPoint,
        nodes,
      );
      const to = getEndpointPosition(
        cable.toNodeId,
        cable.toTerminalId,
        fromPoint,
        nodes,
      );
      if (!from || !to) continue;
      poly = routeOrthogonal(from.point, to.point, from.side, to.side);
    }
    let hit = false;
    for (let i = 0; i < poly.length - 1 && !hit; i++) {
      for (const r of rects) {
        if (segmentInsideRect(poly[i], poly[i + 1], r)) {
          hit = true;
          break;
        }
      }
    }
    if (hit) out.push(cable.id);
  }
  return out;
}

/** New waypoints (interior points, terminals excluded) per cable id. Cables whose
 *  endpoints can't be resolved are silently omitted. */
export type RoutePlanner = typeof tidyRoute;

export function tidySystemCables(
  nodes: SystemNode[],
  cables: Cable[],
  cableIds: string[],
  planner: RoutePlanner = tidyRoute,
): Map<string, Pt[]> {
  const perfId = markStart("tidySystemCables");
  try {
    return tidySystemCablesImpl(nodes, cables, cableIds, planner);
  } finally {
    markEnd(perfId, "tidySystemCables");
  }
}

function tidySystemCablesImpl(
  nodes: SystemNode[],
  cables: Cable[],
  cableIds: string[],
  planner: RoutePlanner,
): Map<string, Pt[]> {
  const obstacles = buildObstacles(nodes);
  const idSet = new Set(cableIds);

  type Resolved = {
    cable: Cable;
    from: { point: Pt; side: Position };
    to: { point: Pt; side: Position };
    co: CableObstacles;
    directLen: number;
  };
  const queue: Resolved[] = [];
  for (const cable of cables) {
    if (!idSet.has(cable.id)) continue;
    const fromPoint = endpointPos(cable.fromNodeId, cable.fromTerminalId, nodes);
    const toPoint = endpointPos(cable.toNodeId, cable.toTerminalId, nodes);
    if (!fromPoint || !toPoint) continue;
    const co = obstaclesForCable(cable, obstacles);
    // For the junction's side pick, the other endpoint's body lives in `self`, not
    // `hard`, so fold it in; its own rect stays out, or every side self-blocks.
    const fromAvoid = [...co.hard];
    const toBody = obstacles.self.get(cable.toNodeId);
    if (toBody) fromAvoid.push(toBody);
    const toAvoid = [...co.hard];
    const fromBody = obstacles.self.get(cable.fromNodeId);
    if (fromBody) toAvoid.push(fromBody);
    const from = getEndpointPosition(
      cable.fromNodeId,
      cable.fromTerminalId,
      toPoint,
      nodes,
      fromAvoid,
    );
    const to = getEndpointPosition(
      cable.toNodeId,
      cable.toTerminalId,
      fromPoint,
      nodes,
      toAvoid,
    );
    if (!from || !to) continue;
    const directLen =
      Math.abs(to.point.x - from.point.x) + Math.abs(to.point.y - from.point.y);
    queue.push({
      cable,
      from,
      to,
      co,
      directLen,
    });
  }
  // Cables outside the tidy scope still have to be respected, or a partial tidy
  // routes as if the rest of the system had no cables. Reconstructed the way
  // cable-edge draws them: stored waypoints, else the plain orthogonal fallback.
  const contextPaths: Pt[][] = [];
  // Reserve slots for connected neighbors even when only one cable is redrawn.
  const terminalFanCables: { cable: Cable }[] = [...queue];
  for (const cable of cables) {
    if (idSet.has(cable.id)) continue;
    const fromPoint = endpointPos(cable.fromNodeId, cable.fromTerminalId, nodes);
    const toPoint = endpointPos(cable.toNodeId, cable.toTerminalId, nodes);
    if (!fromPoint || !toPoint) continue;
    terminalFanCables.push({ cable });
    if (cable.waypoints && cable.waypoints.length > 0) {
      contextPaths.push([
        fromPoint,
        ...cable.waypoints.map((w) => ({ ...w })),
        toPoint,
      ]);
      continue;
    }
    const from = getEndpointPosition(
      cable.fromNodeId,
      cable.fromTerminalId,
      toPoint,
      nodes,
    );
    const to = getEndpointPosition(
      cable.toNodeId,
      cable.toTerminalId,
      fromPoint,
      nodes,
    );
    if (!from || !to) continue;
    contextPaths.push(
      routeOrthogonal(from.point, to.point, from.side, to.side),
    );
  }

  // Long cables first: later short ones tuck around them rather than punch through.
  queue.sort((a, b) => b.directLen - a.directLen);

  // Each bundle follower routes right after its primary, with the primary's
  // just-routed path as a corridor to hug.
  const bundles = detectCableBundles(cables, nodes, idSet);
  const followerToPrimary = new Map<string, string>();
  const primaryToFollowers = new Map<string, string[]>();
  for (const b of bundles) {
    primaryToFollowers.set(b.primaryId, b.followerIds);
    for (const f of b.followerIds) followerToPrimary.set(f, b.primaryId);
  }
  // Followers are skipped here and pulled up after their primary, so nothing is
  // placed twice.
  const ordered: Resolved[] = [];
  const resolvedById = new Map(queue.map((r) => [r.cable.id, r]));
  const placed = new Set<string>();
  for (const entry of queue) {
    if (placed.has(entry.cable.id)) continue;
    if (followerToPrimary.has(entry.cable.id)) continue;
    ordered.push(entry);
    placed.add(entry.cable.id);
    const followers = primaryToFollowers.get(entry.cable.id);
    if (followers) {
      for (const fid of followers) {
        const follower = resolvedById.get(fid);
        if (follower && !placed.has(fid)) {
          ordered.push(follower);
          placed.add(fid);
        }
      }
    }
  }

  const fanOut = planTerminalFanOut(terminalFanCables, nodes);
  const junctionFan = planJunctionFanOut(ordered, nodes);

  const trunks = computeZoneTrunks(nodes);

  const routePass = (
    extraTrunks: Trunk[],
  ): { paths: Map<string, Pt[]>; cablesById: Map<string, Cable> } => {
    const paths = new Map<string, Pt[]>();
    const cablesById = new Map<string, Cable>();
    // Fixed paths join crossing avoidance but deliberately not sharedElbows: a
    // stack on a fixed cable's line is one lane separation could never undo.
    const routedSoFar: Pt[][] = [...contextPaths];
    const sharedElbows = emptyShared();
    const allTrunks = extraTrunks.length > 0 ? [...trunks, ...extraTrunks] : trunks;
    for (const entry of ordered) {
      const splay = fanOut.get(entry.cable.id);
      const fan = junctionFan.get(entry.cable.id);
      // Junctions in a fan group route to the fan anchor; the centre is stitched
      // back on after, so the final segment becomes the diagonal.
      const fromPt = fan?.source ?? entry.from.point;
      const toPt = fan?.target ?? entry.to.point;
      const primaryId = followerToPrimary.get(entry.cable.id);
      const bundleCorridor = primaryId ? paths.get(primaryId) : undefined;
      const trunkCorridors = trunkCorridorsForCable(
        entry.cable,
        allTrunks,
        obstacles.zoneAncestorsByNode,
        { from: entry.from.point, to: entry.to.point },
      );
      const corridors: Pt[][] = bundleCorridor
        ? [bundleCorridor, ...trunkCorridors]
        : trunkCorridors;
      const path = planner(
        fromPt,
        entry.from.side,
        toPt,
        entry.to.side,
        entry.co.hard,
        entry.co.self,
        routedSoFar,
        splay?.source ?? 0,
        splay?.target ?? 0,
        entry.co.soft,
        corridors,
        sharedElbows,
      );
      let stitched = path;
      if (fan?.source) stitched = [entry.from.point, ...stitched];
      if (fan?.target) stitched = [...stitched, entry.to.point];
      paths.set(entry.cable.id, stitched);
      cablesById.set(entry.cable.id, entry.cable);
      routedSoFar.push(stitched);
      accumulateElbows(stitched, sharedElbows);
    }
    return { paths, cablesById };
  };

  // Pass 2 re-routes with the density trunks pass 1 revealed. It costs a second
  // full pass, so it only runs when density actually appears.
  let { paths, cablesById } = routePass([]);
  const densityTrunks = extractDensityTrunks(paths);
  if (densityTrunks.length > 0) {
    ({ paths, cablesById } = routePass(densityTrunks));
  }

  const cablePolarities = new Map<string, RailPolarity>();
  for (const entry of ordered) {
    cablePolarities.set(
      entry.cable.id,
      cableRailPolarity(entry.cable, nodes),
    );
  }

  applyLaneSeparation(
    paths,
    cablesById,
    obstacles,
    junctionFan,
    cablePolarities,
    contextPaths,
  );

  // Crossing repair: each round re-routes the worst cable with the rest held
  // fixed, accepting only a strict improvement. Runs after lane separation so the
  // crossings it sees are the visible ones, not the pre-spread collinear case.
  const repairTrunks =
    densityTrunks.length > 0 ? [...trunks, ...densityTrunks] : trunks;
  // Pairwise counts, patched in place on accept — only the repaired cable's row
  // can change. Repair scores the same joint cost lane separation does; crossings
  // alone makes the two passes adversaries, spreading a bundle and re-stacking it.
  const pairKey = (a: string, b: string): string =>
    a < b ? `${a}|${b}` : `${b}|${a}`;
  const pairCross = new Map<string, number>();
  const pairStack = new Map<string, number>();
  const crossTotals = new Map<string, number>();
  const stackTotals = new Map<string, number>();
  const jointOf = (id: string): number =>
    (crossTotals.get(id) ?? 0) + STACK_WEIGHT * (stackTotals.get(id) ?? 0);
  {
    const ids = Array.from(paths.keys());
    for (const id of ids) {
      crossTotals.set(id, 0);
      stackTotals.set(id, 0);
    }
    for (let i = 0; i < ids.length; i++) {
      const a = paths.get(ids[i])!;
      for (let j = i + 1; j < ids.length; j++) {
        const b = paths.get(ids[j])!;
        const n = countCableCrossings(a, [b]);
        if (n > 0) {
          pairCross.set(pairKey(ids[i], ids[j]), n);
          crossTotals.set(ids[i], crossTotals.get(ids[i])! + n);
          crossTotals.set(ids[j], crossTotals.get(ids[j])! + n);
        }
        const s = countCableStacks(a, [b]);
        if (s > 0) {
          pairStack.set(pairKey(ids[i], ids[j]), s);
          stackTotals.set(ids[i], stackTotals.get(ids[i])! + s);
          stackTotals.set(ids[j], stackTotals.get(ids[j])! + s);
        }
      }
    }
    // Fixed paths never move, so only a repaired cable needs these recounted.
    if (contextPaths.length > 0) {
      for (const [id, path] of paths) {
        const n = countCableCrossings(path, contextPaths);
        if (n > 0) crossTotals.set(id, crossTotals.get(id)! + n);
        const s = countCableStacks(path, contextPaths);
        if (s > 0) stackTotals.set(id, stackTotals.get(id)! + s);
      }
    }
  }
  // A failed attempt is deterministic in the current path set, so skip re-attempts
  // until some path changes.
  let repairEpoch = 0;
  const failedAtEpoch = new Map<string, number>();
  const MAX_REPAIR_ROUNDS = 6;
  for (let round = 0; round < MAX_REPAIR_ROUNDS; round++) {
    const blacklist = new Set<string>();
    let improvedThisRound = false;
    while (true) {
      let worstId: string | null = null;
      let worstCount = 0;
      for (const id of crossTotals.keys()) {
        if (blacklist.has(id)) continue;
        if (failedAtEpoch.get(id) === repairEpoch) continue;
        const n = jointOf(id);
        if (n > worstCount) {
          worstId = id;
          worstCount = n;
        }
      }
      if (!worstId || worstCount === 0) break;
      const entry = resolvedById.get(worstId);
      if (!entry) {
        blacklist.add(worstId);
        continue;
      }
      const others: Pt[][] = [];
      const repairShared = emptyShared();
      for (const [id, path] of paths) {
        if (id === worstId) continue;
        others.push(path);
        accumulateElbows(path, repairShared);
      }
      // Fixed paths join the avoid set but not the shared-elbow set.
      others.push(...contextPaths);
      const splay = fanOut.get(entry.cable.id);
      const fan = junctionFan.get(entry.cable.id);
      const fromPt = fan?.source ?? entry.from.point;
      const toPt = fan?.target ?? entry.to.point;
      const primaryId = followerToPrimary.get(entry.cable.id);
      const bundleCorridor = primaryId ? paths.get(primaryId) : undefined;
      const trunkCorridors = trunkCorridorsForCable(
        entry.cable,
        repairTrunks,
        obstacles.zoneAncestorsByNode,
        { from: entry.from.point, to: entry.to.point },
      );
      const corridors: Pt[][] = bundleCorridor
        ? [bundleCorridor, ...trunkCorridors]
        : trunkCorridors;
      const repaired = planner(
        fromPt,
        entry.from.side,
        toPt,
        entry.to.side,
        entry.co.hard,
        entry.co.self,
        others,
        splay?.source ?? 0,
        splay?.target ?? 0,
        entry.co.soft,
        corridors,
        repairShared,
      );
      let stitched = repaired;
      if (fan?.source) stitched = [entry.from.point, ...stitched];
      if (fan?.target) stitched = [...stitched, entry.to.point];

      let newCross = 0;
      let newStack = 0;
      const newPerOther = new Map<string, number>();
      const newPerOtherStack = new Map<string, number>();
      for (const [otherId, other] of paths) {
        if (otherId === worstId) continue;
        const n = countCableCrossings(stitched, [other]);
        if (n > 0) newPerOther.set(otherId, n);
        newCross += n;
        const s = countCableStacks(stitched, [other]);
        if (s > 0) newPerOtherStack.set(otherId, s);
        newStack += s;
      }
      if (contextPaths.length > 0) {
        newCross += countCableCrossings(stitched, contextPaths);
        newStack += countCableStacks(stitched, contextPaths);
      }
      const newCount = newCross + STACK_WEIGHT * newStack;
      // Repair scores cable-cable crossings only; reject one that enters more bodies.
      const bodyBefore = countCollisions(
        paths.get(worstId)!,
        entry.co.hard,
        entry.co.self,
      ).body;
      const bodyAfter = countCollisions(stitched, entry.co.hard, entry.co.self).body;
      if (newCount >= worstCount || bodyAfter > bodyBefore) {
        blacklist.add(worstId);
        failedAtEpoch.set(worstId, repairEpoch);
        continue;
      }
      repairEpoch++;
      paths.set(worstId, stitched);
      for (const otherId of paths.keys()) {
        if (otherId === worstId) continue;
        const key = pairKey(worstId, otherId);
        const oldN = pairCross.get(key) ?? 0;
        const newN = newPerOther.get(otherId) ?? 0;
        if (newN !== oldN) {
          crossTotals.set(otherId, crossTotals.get(otherId)! - oldN + newN);
          if (newN > 0) pairCross.set(key, newN);
          else pairCross.delete(key);
        }
        const oldS = pairStack.get(key) ?? 0;
        const newS = newPerOtherStack.get(otherId) ?? 0;
        if (newS !== oldS) {
          stackTotals.set(otherId, stackTotals.get(otherId)! - oldS + newS);
          if (newS > 0) pairStack.set(key, newS);
          else pairStack.delete(key);
        }
      }
      crossTotals.set(worstId, newCross);
      stackTotals.set(worstId, newStack);
      improvedThisRound = true;
    }
    if (!improvedThisRound) break;
  }

  // Repair may have landed two cables on the same detour line; spread them again.
  applyLaneSeparation(
    paths,
    cablesById,
    obstacles,
    junctionFan,
    cablePolarities,
    contextPaths,
    true,
  );

  // Detour clamp. Nothing above bounds how far a cable is dragged from its direct
  // path — repair detours and block slides together turn an 80 px hop into a
  // ~760 px excursion. Runs last so nothing re-inflates it.
  for (const entry of ordered) {
    const path = paths.get(entry.cable.id);
    if (!path) continue;
    const len = polyLength(path);
    const direct = entry.directLen;
    if (
      direct > 0 &&
      len > direct * DETOUR_CLAMP_RATIO &&
      len - direct > DETOUR_CLAMP_SLACK_PX
    ) {
      const splay = fanOut.get(entry.cable.id);
      const fan = junctionFan.get(entry.cable.id);
      const fromPt = fan?.source ?? entry.from.point;
      const toPt = fan?.target ?? entry.to.point;
      let clean = planner(
        fromPt,
        entry.from.side,
        toPt,
        entry.to.side,
        entry.co.hard,
        entry.co.self,
        [],
        splay?.source ?? 0,
        splay?.target ?? 0,
        entry.co.soft,
        [],
      );
      if (fan?.source) clean = [entry.from.point, ...clean];
      if (fan?.target) clean = [...clean, entry.to.point];
      // The un-bundled route is scored without crossing context, so it can land on
      // a body the detour was avoiding.
      const cleanBody = countCollisions(clean, entry.co.hard, entry.co.self).body;
      const pathBody = countCollisions(path, entry.co.hard, entry.co.self).body;
      if (polyLength(clean) < len && cleanBody <= pathBody) {
        paths.set(entry.cable.id, clean);
      }
    }
  }

  const out = new Map<string, Pt[]>();
  for (const [id, path] of paths) {
    // Re-simplify in case lane offsets created collinear runs.
    const simplified = simplifyOrthogonal(path);
    out.set(id, simplified.slice(1, -1).map((p) => ({ ...p })));
  }
  return out;
}
